<script setup lang="ts">
import { ref, computed, watch, reactive, onMounted } from 'vue';
import * as clienteService from '@/services/clientesService';

interface Props {
  modelValue?: number | null,
  placeholder?: string
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: 'Digite o nome do cliente',
})

const emit = defineEmits(['update:modelValue', 'selected'])

const query = ref("")
const showSuggestions = ref(false)
const highlightedIndex = ref(-1)
const selecionados = ref<clienteService.Cliente | null>(null)

var controle = reactive({
  itens: [] as clienteService.Cliente[],
  text: "",
  status: "",
  pesquisando: false,
})

onMounted(() => {
  buscar()
})

const filtrados = computed(() => {
  const q = String(query.value).trim().toLowerCase()
  if (!q) return []


  var r = controle.itens?.filter(item =>
    item.nome.toLowerCase().includes(q) ||
    item.cpf?.toLowerCase().includes(q)
  );

  return r;
})

const placeHolder = computed(() => {
  if (controle.pesquisando) {
    return "Buscando clientes..."
  }

  return props.placeholder
})

// Computed para exibir o texto no input
const displayText = computed(() => {
  if (selecionados.value) {
    return `${selecionados.value.nome} - ${selecionados.value.cpf}`
  }
  return query.value
})

function onInput() {
  // Limpar cidade selecionada quando o usuário digitar
  if (selecionados.value) {
    selecionados.value = null
    emit('update:modelValue', undefined)
  }

  showSuggestions.value = true
  highlightedIndex.value = -1
}

function seleciona(item: clienteService.Cliente) {
  selecionados.value = item
  query.value = "" // Limpar query para não interferir no displayText
  showSuggestions.value = false
  highlightedIndex.value = -1

  emit('update:modelValue', item.clienteId)
  emit('selected', item)
}

function hideSuggestions() {
  // Delay to allow click event on suggestion
  setTimeout(() => {
    showSuggestions.value = false
    highlightedIndex.value = -1
  }, 150)
}

function handleKeyDown(event: KeyboardEvent) {
  if (!showSuggestions.value || !filtrados.value.length) return

  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault()
      highlightedIndex.value = Math.min(
        highlightedIndex.value + 1,
        filtrados.value.length - 1
      )
      break

    case 'ArrowUp':
      event.preventDefault()
      highlightedIndex.value = Math.max(highlightedIndex.value - 1, 0)
      break

    case 'Enter':
      event.preventDefault()
      if (highlightedIndex.value >= 0 && highlightedIndex.value < filtrados.value.length) {
        seleciona(filtrados.value[highlightedIndex.value])
      }
      break

    case 'Escape':
      event.preventDefault()
      showSuggestions.value = false
      highlightedIndex.value = -1
      break
  }
}

// Watch para sincronizar com modelValue externo
watch(() => props.modelValue, (newValue) => {
  console.log("modelValue mudou para:", newValue);
  if (newValue && controle.itens.length > 0) {
    // Buscar o cliente pelo ID
    const cliente = controle.itens.find(c => c.clienteId === newValue)
    if (cliente) {
      selecionados.value = cliente
      query.value = ""
    }
  } else if (!newValue) {
    selecionados.value = null
    query.value = ""
  }
})

// Watch para buscar cliente quando o modelValue muda e ainda não temos os clientes
watch(() => controle.itens, (clientes) => {
  if (props.modelValue && clientes.length > 0) {
    const cliente = clientes.find(c => c.clienteId === props.modelValue)
    if (cliente) {
      selecionados.value = cliente
      query.value = ""
    }
  }
})

// Reset highlighted index when filtered cities change
watch(filtrados, () => {
  highlightedIndex.value = -1
})

function buscar() {
  controle.status = "Buscando clientes...";
  controle.pesquisando = true;

  clienteService.obter({ tamanhoPagina: 6000 }).then(response => {
    controle.itens = response.dados || [];
    controle.status = "";
    controle.pesquisando = false;
  }).catch(error => {
    console.error("Erro ao buscar clientes:", error);
    controle.status = `Erro ${error.message}`;
  })
}
</script>

<template>
  <div class="autocomplete-cidade">
    <input type="text" :value="displayText" @input="(e) => { query = (e.target as HTMLInputElement).value; onInput(); }"
      @focus="showSuggestions = true" @blur="hideSuggestions" @keydown="handleKeyDown" :placeholder="placeHolder"
      class="autocomplete-input form-control form-control-sm" :readonly="controle.pesquisando == true " />

    <ul v-if="showSuggestions && filtrados.length" class="suggestions-list">
      <li v-for="(item, index) in filtrados" :key="item.clienteId" @mousedown.prevent="seleciona(item)"
        @mouseenter="highlightedIndex = index" :class="{
          'suggestion-item': true,
          'highlighted': index === highlightedIndex
        }">
        <strong>{{ item.nome }}</strong>
        <span class="city-uf"> - {{ item.cpf }}</span>
      </li>
    </ul>
  </div>
</template>

<style scoped>
.autocomplete-cidade {
  position: relative;
  width: 100%;
}

.autocomplete-input {
  width: 100%;
  padding: 8px 12px;
  box-sizing: border-box;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 14px;
}

.autocomplete-input:focus {
  outline: none;
  border-color: #0d6efd;
  box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
}

.suggestions-list {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #ced4da;
  border-top: none;
  border-radius: 0 0 4px 4px;
  max-height: 180px;
  overflow-y: auto;
  margin: 0;
  padding: 0;
  list-style: none;
  z-index: 1000;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.suggestion-item {
  padding: 8px 12px;
  cursor: pointer;
  border-bottom: 1px solid #f8f9fa;
  font-size: 14px;
  transition: background-color 0.2s ease;
}

.suggestion-item:hover,
.suggestion-item.highlighted {
  background: #e3f2fd;
}

.suggestion-item:last-child {
  border-bottom: none;
}

.city-uf {
  color: #6c757d;
  font-size: 12px;
}
</style>